import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/common/ScrollToTop';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import CreateMapPage from './pages/CreateMapPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import ContactPage from './pages/ContactPage';
import FAQPage from './pages/FAQPage';
import GalleryPage from './pages/GalleryPage';
import NotFoundPage from './pages/NotFoundPage';
import PrivacyPage from './pages/PrivacyPage';
import ProductsPage from './pages/ProductsPage';
import RefundPage from './pages/RefundPage';
import TermsPage from './pages/TermsPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ProfilePage from './pages/ProfilePage';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentFailed from './pages/PaymentFailed';
import PaymentIframeSuccess from './pages/PaymentIframeSuccess';
import Checkout from './pages/Checkout';
import AuthProvider from './components/auth/AuthProvider';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AuthModals from './components/auth/AuthModals';
import CookiesPage from './pages/CookiesPage';
import GiftCardPage from './pages/GiftCardPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import StorageDiagnosticsPage from './pages/StorageDiagnosticsPage';
import { CurrencyProvider } from './contexts/CurrencyContext';

// Import geocoding test utilities for development
import './utils/testGeocoding';

// Import localStorage cleanup
import { performLocalStorageCleanup } from './utils/storageUtils';

function App() {
    // Clean up localStorage on app startup
    React.useEffect(() => {
        const cleanupLocalStorage = async () => {
            try {
                const freedSpace = await performLocalStorageCleanup();
                if (freedSpace > 0) {
                    console.log(`🧹 App startup: Cleaned ${(freedSpace / (1024 * 1024)).toFixed(2)}MB from localStorage`);
                }
            } catch (error) {
                console.warn('App startup localStorage cleanup failed:', error);
            }
        };

        cleanupLocalStorage();
    }, []);

    return (
        <CurrencyProvider>
            <AuthProvider>
                <div className="app-wrapper max-w-[100vw] overflow-x-hidden">
                    <Toaster
                        position="bottom-right"
                        toastOptions={{
                            duration: 3000,
                            style: {
                                background: '#363636',
                                color: '#fff',
                            },
                            success: {
                                duration: 3000,
                                style: {
                                    background: '#22c55e',
                                    color: '#fff',
                                },
                            },
                            error: {
                                duration: 4000,
                                style: {
                                    background: '#ef4444',
                                    color: '#fff',
                                },
                            },
                        }}
                    />
                    <div className="flex flex-col min-h-screen bg-background max-w-[100vw] overflow-x-hidden">
                        <Header />
                        <AuthModals />
                        <main className="flex-grow w-full overflow-x-hidden">
                            <ScrollToTop />
                            <Routes>
                                <Route path="/" element={<HomePage />} />
                                <Route path="/create" element={<CreateMapPage />} />
                                <Route path="/cart" element={<CartPage />} />
                                <Route path="/checkout" element={<Checkout />} />
                                <Route path="/about" element={<AboutPage />} />
                                <Route path="/contact" element={<ContactPage />} />
                                <Route path="/faq" element={<FAQPage />} />
                                <Route path="/privacy" element={<PrivacyPage />} />
                                <Route path="/terms" element={<TermsPage />} />
                                <Route path="/refund" element={<RefundPage />} />
                                <Route path="/cookies" element={<CookiesPage />} />
                                <Route path="/gallery" element={<GalleryPage />} />
                                <Route path="/products" element={<ProductsPage />} />
                                <Route path="/gift-card" element={<GiftCardPage />} />
                                <Route path="/login" element={<LoginPage />} />
                                <Route path="/register" element={<RegisterPage />} />
                                <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
                                <Route path="/storage-diagnostics" element={<StorageDiagnosticsPage />} />

                                {/* Payment routes */}
                                <Route path="/payment/success" element={<PaymentSuccess />} />
                                <Route path="/payment/failed" element={<PaymentFailed />} />
                                <Route path="/payment-iframe-success" element={<PaymentIframeSuccess />} />

                                <Route
                                    path="/profile"
                                    element={
                                        <ProtectedRoute>
                                            <ProfilePage />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route path="*" element={<NotFoundPage />} />
                            </Routes>
                        </main>
                        <Footer />
                    </div>
                </div>
            </AuthProvider>
        </CurrencyProvider>
    );
}

export default App; 